import {
    createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react'

const EMPTY_WORKSPACE = {
    workspaceMode: null,
    enterWorkspace: () => {},
    exitWorkspace: () => {},
}

export const LayoutWorkspaceContext = createContext(EMPTY_WORKSPACE)

export function useLayoutWorkspace() {
    return useContext(LayoutWorkspaceContext)
}

export function useExitWorkspaceOnUnmount() {
    const { exitWorkspace } = useLayoutWorkspace()
    useEffect(() => () => {
        exitWorkspace({ preserveScroll: false })
    }, [exitWorkspace])
}

export function useExitWorkspaceWhenUnavailable(unavailable) {
    const { exitWorkspace } = useLayoutWorkspace()
    useEffect(() => {
        if (unavailable) exitWorkspace({ preserveScroll: false })
    }, [unavailable, exitWorkspace])
}

const ACTIVE_OVERLAY_SELECTOR = [
    '[data-app-overlay]',
    '.modal-overlay',
    '[role="dialog"]',
    '[role="listbox"]',
    '[role="menu"]',
].join(', ')

function isVisibleOverlay(element) {
    let current = element
    while (current) {
        if (current.hidden || current.getAttribute('aria-hidden') === 'true') return false
        const style = window.getComputedStyle(current)
        if (style.display === 'none'
            || style.visibility === 'hidden'
            || style.visibility === 'collapse'
            || style.opacity === '0') return false
        current = current.parentElement
    }
    return true
}

function hasActiveOverlay(eventTarget) {
    if (eventTarget instanceof Element && eventTarget.closest(
        'input, textarea, select, [contenteditable="true"], [role="combobox"]',
    )) return true
    return [...document.querySelectorAll(ACTIVE_OVERLAY_SELECTOR)].some(isVisibleOverlay)
}

export function useLayoutWorkspaceMode(setMobileNavOpen) {
    const [workspaceMode, setWorkspaceMode] = useState(null)
    const workspaceModeRef = useRef(null)
    const workspaceScrollRef = useRef(null)

    const setModeKeepingScroll = useCallback((nextMode, { preserveScroll = true } = {}) => {
        if (workspaceModeRef.current === nextMode) {
            return
        }
        workspaceScrollRef.current = preserveScroll
            ? { left: window.scrollX, top: window.scrollY }
            : null
        workspaceModeRef.current = nextMode
        setWorkspaceMode(nextMode)
    }, [])

    const enterWorkspace = useCallback((mode) => {
        setModeKeepingScroll(mode)
    }, [setModeKeepingScroll])

    const exitWorkspace = useCallback((options) => {
        setModeKeepingScroll(null, options)
    }, [setModeKeepingScroll])

    useLayoutEffect(() => {
        const position = workspaceScrollRef.current
        if (!position) return undefined
        workspaceScrollRef.current = null
        window.scrollTo({ ...position, behavior: 'auto' })
        return undefined
    }, [workspaceMode])

    useEffect(() => {
        if (!workspaceMode) return undefined
        setMobileNavOpen(false)
        const onKey = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented || hasActiveOverlay(event.target)) return
            event.preventDefault()
            exitWorkspace()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [workspaceMode, setMobileNavOpen, exitWorkspace])

    useEffect(() => {
        if (!workspaceMode || typeof window.matchMedia !== 'function') return undefined
        const mobileViewport = window.matchMedia('(max-width: 1024px)')
        const exitOnMobile = (event) => {
            if (event.matches) exitWorkspace({ preserveScroll: false })
        }
        exitOnMobile(mobileViewport)
        mobileViewport.addEventListener('change', exitOnMobile)
        return () => mobileViewport.removeEventListener('change', exitOnMobile)
    }, [workspaceMode, exitWorkspace])

    const workspaceContextValue = useMemo(() => ({
        workspaceMode,
        enterWorkspace,
        exitWorkspace,
    }), [workspaceMode, enterWorkspace, exitWorkspace])

    return { workspaceMode, workspaceContextValue }
}
